import { createApi } from '@reduxjs/toolkit/query/react';

import deserialize from 'helpers/getroPropertyMapper';
import { baseQuery } from '../queries';
import { companiesApi } from '../companies';

const networkBaseQueryV2 = ({ url, method = 'get', data, params, options = {} }) =>
  baseQuery({ url, method, data, params, options, version: 'v2' });

export const organizationApiV2 = createApi({
  reducerPath: 'organizationApiV2',
  baseQuery: networkBaseQueryV2,
  tagTypes: ['subscriptions'],
  endpoints: (builder) => ({
    getIndustry: builder.query({
      query: (params) => ({
        url: '/industry_tags',
        params,
      }),
    }),
    getChargeBessSession: builder.mutation({
      query: (networkId) => ({
        url: `/collections/${networkId}/chargebee_sessions`,
        method: 'post',
      }),
      transformResponse: ({ portalSession }) => portalSession,
    }),
    getSubscriptions: builder.query({
      query: (networkId) => ({
        url: `/collections/${networkId}/subscriptions`,
      }),
      providesTags: ['subscriptions'],
    }),
    getCustomFilters: builder.query({
      query: (id) => ({
        url: `/collections/${id}/custom_filters`,
      }),
      transformResponse: ({ data }) => data,
    }),
    getNetwork: builder.query({
      query: (id) => ({
        url: `/collections/${id}`,
      }),
      transformResponse: ({ data: { attributes, id } }) => ({
        ...attributes,
        id,
      }),
    }),
    updateFilterVisibility: builder.mutation({
      query: ({ data, networkId }) => ({
        url: `/collections/${networkId}/filters_visibility`,
        method: 'put',
        data: {
          ...data.jobBoardFilters,
          customFilters: data.customFilters,
        },
      }),
      onQueryStarted: async ({ data, networkId }, { dispatch, queryFulfilled }) => {
        const networkPatchResult = dispatch(
          organizationApiV2.util.updateQueryData('getNetwork', networkId, (draft) => {
            Object.assign(draft, {
              ...draft,
              jobBoardFilters: data.jobBoardFilters,
            });
          }),
        );
        const filtersPatchResult = dispatch(
          organizationApiV2.util.updateQueryData('getCustomFilters', networkId, (draft) => {
            const mapData = new Map(data.customFilters.map((item) => [item.id.toString(), item]));

            Object.assign(
              draft,
              draft.map((item) => {
                const visibility = mapData.get(item.id)?.visibility || item.attributes.visibility;
                return {
                  ...item,
                  attributes: {
                    ...item.attributes,
                    visibility,
                  },
                };
              }),
            );
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          filtersPatchResult.undo();
          networkPatchResult.undo();
        }
      },
    }),
    downloadJobApplication: builder.mutation({
      query: (networkId) => ({
        url: `/collections/${networkId}/job_applications/export_all_csv`,
        method: 'post',
      }),
    }),
    downloadLikelyApplied: builder.query({
      query: ({ networkId, startDate, endDate }) => ({
        url: `/collections/${networkId}/analytics/jobs_likely_applied_csv`,
        params: {
          startDate,
          endDate,
        },
      }),
    }),
    updateNetworkKind: builder.mutation({
      query: ({ networkId, kind }) => ({
        url: `/collections/${networkId}/settings/kind`,
        method: 'patch',
        data: { kind },
      }),
    }),
    claimOrganization: builder.mutation({
      query: ({ collectionId, organizationId }) => ({
        url: `/collections/${collectionId}/organizations/${organizationId}/claim`,
        method: 'post',
      }),
      transformResponse: (response) => {
        const data = deserialize(response);
        data.companyCollection = deserialize(data.companyCollection);
        return data;
      },
    }),
    getIntroRequests: builder.query({
      query: (networkId) => ({
        url: `/collections/${networkId}/analytics/intro_requests`,
      }),
    }),
    getSharedLists: builder.query({
      query: (networkId) => ({
        url: `/collections/${networkId}/analytics/shared_lists`,
      }),
    }),
    addCompanyByName: builder.mutation({
      query: ({ networkId, name }) => ({
        url: `/collections/${networkId}/organizations`,
        method: 'post',
        data: { name },
      }),
      transformResponse: ({ data: { id, attributes } }) => ({ id, ...attributes }),
      invalidatesTags: ['subscriptions'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(companiesApi.util.invalidateTags(['companies']));
        } catch {
          // no-op
        }
      },
    }),
    bulkAddCompanyByDomain: builder.mutation({
      query: ({ networkId, domains }) => ({
        url: `/collections/${networkId}/organizations/bulk_create`,
        method: 'post',
        data: { domains },
      }),
      invalidatesTags: ['subscriptions'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(companiesApi.util.invalidateTags(['companies']));
        } catch {
          // no-op
        }
      },
    }),
    exportJobAlertSubscribers: builder.mutation({
      query: (networkId) => ({
        url: `/collections/${networkId}/job_alerts/export_csv`,
        method: 'post',
      }),
    }),
  }),
});

export const {
  useGetIndustryQuery,
  useLazyGetIndustryQuery,
  useGetChargeBessSessionMutation,
  useGetSubscriptionsQuery,
  useDownloadJobApplicationMutation,
  useLazyDownloadLikelyAppliedQuery,
  useGetCustomFiltersQuery,
  useGetNetworkQuery,
  useLazyGetNetworkQuery,
  useUpdateFilterVisibilityMutation,
  useUpdateNetworkKindMutation,
  useClaimOrganizationMutation,
  useGetIntroRequestsQuery,
  useGetSharedListsQuery,
  useAddCompanyByNameMutation,
  useBulkAddCompanyByDomainMutation,
  useExportJobAlertSubscribersMutation,
} = organizationApiV2;
